import anime from "animejs";
import { useApplicationStore } from "@/store";
import { Ref, nextTick } from "vue";

let skipped = false;
let done = false;

const loadingDoneTimeline = anime.timeline({
  easing: "easeOutExpo",
  duration: 750,
});
loadingDoneTimeline.pause();

// should get called when app mounted
export function appMounted(props: { fillLogoRef: Ref<boolean> }) {
  // get application store instance to check if users has set 'reduced motion' or 'skip entry animation'
  const applicationStore = useApplicationStore();

  // rittal logo outline animation
  anime({
    targets: "#rittal-logo path",
    strokeDashoffset: [anime.setDashoffset, 0],
    easing: "easeInOutSine",
    duration: 700,
    delay: function (el, i) {
      return i * 200;
    },
    loop: false,
  }).finished.then(() => {
    props.fillLogoRef.value = true;

    if (!done && !skipped) {
      skipped = true;
      loadingDoneTimeline.play();
    }
  });

  // add animation timeline components her, because i need to access reduced motion state
  // animation for hiding loading scrim and display input cards
  if (!applicationStore.reducedMotion && !applicationStore.skipEntryAnimation) {
    loadingDoneTimeline
      // position rittal logo back to the top left
      .add({
        targets: "#rittal-logo",
        left: ["50vw", "0vw"],
        top: ["50vh", "0vh"],
        translateX: ["-50%", "0%"],
        translateY: ["-50%", "0%"],
        scale: [2, 1],
        easing: "easeInQuad",
        duration: applicationStore.reducedMotion ? 10 : 200,
      });
  }
  // fade out the scrim
  loadingDoneTimeline.add({
    targets: "#scrim",
    easing: "easeOutQuad",
    opacity: [1, 0],
    duration: applicationStore.reducedMotion ? 10 : 200,
  });

  // return if user prefers reduced motion
  if (applicationStore.reducedMotion || applicationStore.skipEntryAnimation) {
    props.fillLogoRef.value = true;
    return;
  }

  // append to exit animation
  loadingDoneTimeline
    // fade in input card for supplier and material
    .add({
      targets: "#inputs-card",
      opacity: [0, 1],
      scaleY: ["0%", "100%"],
      duration: 300,
    })
    // scale in the supplier input element
    .add(
      {
        targets: "#select-supplier",
        opacity: [0, 1],
        "max-width": ["0%", "100%"],
        duration: 300,
      },
      "-=150"
    )
    // scale in the material input element
    .add(
      {
        targets: "#select-matnr",
        opacity: [0, 1],
        "max-width": ["0%", "100%"],
        duration: 300,
      },
      "-=150"
    )
    // slide in the footer element
    .add({
      targets: "footer p",
      translateY: [50, 0],
      delay: anime.stagger(100), // increase delay by 100ms for each elements.
    });
  // pause the timeline so that its not playing instantly
  loadingDoneTimeline.pause();
}

export async function skipAnimation() {
  // ignore if already skipped
  if (skipped || done) return;
  skipped = true;
  // play timeline
  loadingDoneTimeline.play();
}

// should be called when systems are loaded and user can see app
export async function appLoaded() {
  // if it was skipped before loaded then just return
  if (skipped || done) return;
  done = true;
  // play skip animation
  loadingDoneTimeline.play();
}

// should be called when the material was checked successfully
export async function afterSuccessfulMaterialInput() {
  // wait one tick to let the dom actually update
  await nextTick();
  // get application store to access 'reduced motion' state
  const applicationStore = useApplicationStore();
  // return if user prefers reduced motion
  if (applicationStore.reducedMotion || applicationStore.skipEntryAnimation)
    return;

  // build timeline
  anime
    .timeline({
      easing: "easeOutExpo",
      duration: 750,
    })
    // animate wrapper card
    .add({
      targets: "#view-card",
      scaleY: ["0%", "100%"],
      duration: 300,
    })
    // the three view-selection buttons ('print', 'cancel' and 'pdf')
    .add(
      {
        targets: ".v-slide-group__content button",
        translateY: [50, 0],
        delay: anime.stagger(100),
        duration: 200,
      },
      "-=100"
    )
    // the content box underneath the three buttons
    .add(
      {
        targets: ".v-window ",
        opacity: [0, 1],
        duration: 600,
        translateY: [-100, 0],
      },
      "-=150"
    );
}
